import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  List,
  ListItem,
  Grid2,
} from '@mui/material';
import { FilterList, Search, AccessTime, CheckCircleOutline } from '@mui/icons-material';
import { WithdrawalContext } from '../contexts/WithdrawalContext';
import { formatAmount, convertUtcByCurrency } from '../utils/utils';
import LoadingState from '../components/LoadingState';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';

const PAGE_SIZE = 20;
const statusConfig = {
  FAProcessing: {
    icon: AccessTime,
    text: '待处理',
    color: '#4581c9',
  },
  Success: {
    icon: CheckCircleOutline,
    text: '成功',
    color: '#3a7c49',
  },
};

const StatusDisplay = ({ status }) => {
  const { icon: StatusIcon, text, color } = statusConfig[status] || {};

  return (
    <Box sx={{ textAlign: 'center' }}>
      {StatusIcon && (
        <StatusIcon sx={{ color, height: '1.75rem', width: '1.75rem' }} />
      )}
      <Typography variant="body2" sx={{ color , fontWeight: 'bolder'}}>
        {text || 'Unknown'}
      </Typography>
    </Box>
  );
};

const PayoutHistory = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const { 
    withdrawals, 
    totalCount,
    fetchWithdrawals, 
    resetWithdrawals 
  } = useContext(WithdrawalContext);


  const loadWithdrawals = async (pageNumber, search) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await fetchWithdrawals(
        [4, 8],
        null,
        search || null,
        pageNumber,
        PAGE_SIZE,
        'DESC'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadWithdrawals(1, null);
    return () => resetWithdrawals(); 
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

   const {
    data: paginatedWithdrawals,
    totalCount: effectiveTotal,
  } = usePagination({
    data: withdrawals,
    totalCount,
    page: currentPage,
    pageSize: PAGE_SIZE,
    mode: 'server',
  });

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSearchTrigger = () => {
    setCurrentPage(1);
    loadWithdrawals(1, searchQuery);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    loadWithdrawals(newPage, searchQuery);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
        minHeight: 0,
        width: '100%',
      }}
    >
      {/* Search Field */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: 1,
          color: 'var(--secondary-color)'
        }}
      >
        <FilterList  />
        <TextField
          placeholder="搜索单号"
          variant="standard"
          fullWidth
          slotProps={{
            input: {
                disableUnderline: true,
                style: { color: '#fff' },
                endAdornment:
                    <Search sx={{ color: 'var(--secondary-color)' }} />   
            }
          }}
          sx={{ marginLeft: 2, marginRight: 2 }}
          value={searchQuery}
          onChange={handleSearchChange}
          onBlur={handleSearchTrigger}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSearchTrigger();
          }}
        />
      </Box>

      {isLoading && (
        <LoadingState fullHeight />
      )}

      {/* Scrollable List */}
      {!isLoading && (
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            borderRadius: 2,
            backgroundColor: 'var(--bg-secondary-color)',
            marginTop: 1,
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              minHeight: 0,
              padding: '20px 16px',
            }}
          >
            {paginatedWithdrawals.length === 0 && (
              <Typography
                variant="h6"
                color="var(--secondary-color)"
                sx={{ textAlign: 'center' }}
              >
                没有匹配的记录
              </Typography>
            )}
            {paginatedWithdrawals.length > 0 && (
              <List sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  padding: 0,
                }}
              >
                {paginatedWithdrawals.map((withdrawal, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      backgroundColor: 'transparent',
                      boxShadow: '0 4px 10px 2px rgba(0, 0, 0, 1)',
                      color: 'var(--secondary-color)',
                      borderRadius: 2,
                      padding: {
                        xs: '0.5rem 1rem !important',
                        md: '0.75rem 2rem 0.75rem 1rem !important',
                      },
                    }}
                  >
                    <Grid2 container spacing={2} alignItems="center" sx={{ flex: 1 }}>
                      <Grid2 size={{ xs: 2, md: 1 }}>
                        <Box>
                          <StatusDisplay status={withdrawal.status} />
                        </Box>
                      </Grid2>
                      <Grid2 size={{ xs: 10, md: 11 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row-reverse', justifyContent: 'flex-start' }}>
                          <Typography variant='body2' >{ withdrawal.orderId }</Typography>
                          <Typography variant='body2' sx={{ marginRight: 2 }}>订单号</Typography>
                        </Box>
                        <Box sx={{ display: 'flex' }}>
                          <Typography sx={{ 
                            color: '#fff', 
                            fontSize: {
                              xs: '1.5rem',
                              md: '1.75rem',
                            }, }} >
                            { formatAmount(withdrawal.amount) }
                          </Typography>
                          <Typography variant='body2' sx={{ marginLeft: 2, alignSelf: 'center' }}>金额</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row-reverse', justifyContent: 'flex-start' }}>
                          <Typography variant='body2'>{ convertUtcByCurrency(withdrawal.updatedAt, 'DD/MM/YYYY HH:mm:ss') }</Typography>
                        </Box>
                      </Grid2>
                    </Grid2>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
          <Pagination
            currentPage={currentPage}
            totalCount={effectiveTotal}
            onPageChange={handlePageChange}
            pageSize={PAGE_SIZE}
          />
        </Box>
      )}
    </Box>
  );
};

export default PayoutHistory;